import { Link, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatProjectNumber,
  formatPoNumber,
  formatMinutes,
  type CostingBasis,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { PageRoot } from '../ui/Page';
import { BackCrumb, DetailHeader, MetaStrip, SectionCard } from '../ui/Detail';
import { StatusBadge, Notice, type Tone } from '../ui/Badge';
import { Button } from '../ui/Button';
import { can } from '@servwell/shared';
import { useSession } from '../session';
import { InlineRename } from '../ui/InlineRename';
import { dash } from '@/components/ui/data-table';
import { EmptyState } from '../ui/EmptyState';
import { ErrorState } from '../ui/QueryStates';
import { PageSkeleton } from '../ui/Skeleton';
import { money } from '../money';
import { COLLECTED_BASIS, revenueFigure } from '../costing/basis';
import { JOB_STATUS, QUOTE_STATUS, INVOICE_STATUS, display } from '../status';
import { PROJECT_STATUS } from './ProjectsPage';
import { IconProjects } from '../icons';

const PO_STATUS: Record<string, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  sent: { label: 'Ordered', tone: 'info' },
  partially_received: { label: 'Part arrived', tone: 'warn' },
  closed: { label: 'Received', tone: 'success' },
  canceled: { label: 'Canceled', tone: 'neutral' },
};

/** The buttons that actually exist from here — never a menu of every state. */
const NEXT_MOVE: Record<string, { via: string; label: string }[]> = {
  planned: [
    { via: 'project.start', label: 'Start the project' },
    { via: 'project.cancel', label: 'Cancel' },
  ],
  active: [
    { via: 'project.close', label: 'Close it out' },
    { via: 'project.hold', label: 'Put on hold' },
  ],
  on_hold: [
    { via: 'project.resume', label: 'Resume' },
    { via: 'project.close', label: 'Close it out' },
  ],
  closed: [{ via: 'project.reopen', label: 'Reopen' }],
  canceled: [],
};

/**
 * One project (PRD v2 §6.8): everything hanging off it, and the P&L that is the only
 * reason to open one.
 *
 * The money panel states its basis out loud and keeps COMMITTED purchase-order money out
 * of cost — the dollars are spoken for, but counting them as spent now and again when the
 * parts land on a job would bill one dollar to one project twice. It's shown as a separate
 * "projected" line instead, which is the number an owner mid-build actually wants.
 */
export function ProjectDetailPage() {
  const { projectId } = useParams({ from: '/office/projects/$projectId' });
  const trpc = useTRPC();
  const qc = useQueryClient();
  const q = useQuery(trpc.project.get.queryOptions({ projectId }));
  const canManage = can(useSession().role, 'project', 'manage');

  const rename = useMutation(
    trpc.project.update.mutationOptions({ onSuccess: () => void qc.invalidateQueries() }),
  );
  const move = useMutation(
    trpc.project.transition.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );

  if (q.isPending) return <PageSkeleton />;
  if (q.isError) {
    return (
      <PageRoot>
        <BackCrumb to="/projects" label="Projects" />
        <ErrorState
          icon={IconProjects}
          what="this project"
          onRetry={() => void q.refetch()}
        />
      </PageRoot>
    );
  }
  if (!q.data) {
    // Gone is not broken — an EmptyState says so without offering a retry that can't help.
    return (
      <PageRoot>
        <BackCrumb to="/projects" label="Projects" />
        <EmptyState
          icon={IconProjects}
          title="That project isn't here"
          description="It may have been deleted, or the link is out of date."
        />
      </PageRoot>
    );
  }

  const {
    project,
    customer,
    propertyLabel,
    jobs,
    quotes,
    invoices,
    purchaseOrders,
    rollup,
    unratedMinutes,
    paidCents,
    withheld,
  } = q.data;
  const quotesWithheld = withheld.find((entry) => entry.field === 'quotes');
  const s = PROJECT_STATUS[project.status] ?? {
    label: project.status,
    tone: 'neutral' as Tone,
  };

  return (
    <PageRoot>
      <BackCrumb to="/projects" label="Projects" />
      <DetailHeader
        title={project.name}
        badge={<StatusBadge tone={s.tone}>{s.label}</StatusBadge>}
        right={
          /* Read-only for a bookkeeper: they hold `project:read` (the P&L view) and not
             `project:manage`, so the rename and the status moves are simply not drawn. */
          !canManage ? null : (
            <>
              {/* A project outlives the name somebody typed on day one — "Miller build"
                becomes "Miller — Phase 2" the moment a second phase exists. */}
              <InlineRename
                value={project.name}
                busy={rename.isPending}
                onSave={(name) => rename.mutate({ projectId, name, ...idem() })}
              />
              {(NEXT_MOVE[project.status] ?? []).map((m, i) => (
                <Button
                  key={m.via}
                  variant={i === 0 ? 'primary' : 'ghost'}
                  disabled={move.isPending}
                  onClick={() =>
                    move.mutate({
                      projectId,
                      via: m.via as 'project.start',
                      ...idem(),
                    })
                  }
                >
                  {m.label}
                </Button>
              ))}
            </>
          )
        }
      />

      <MetaStrip
        items={[
          { label: 'Project', value: formatProjectNumber(project.projectNumber) },
          {
            label: 'Customer',
            value: (
              <Link
                to="/customers/$customerId"
                params={{ customerId: customer.id }}
                className="underline decoration-dotted"
              >
                {customer.displayName}
              </Link>
            ),
          },
          { label: 'Where', value: propertyLabel ?? 'Across their properties' },
          { label: 'Starts', value: dash(project.startsOn) },
          { label: 'Target end', value: dash(project.targetEndOn) },
        ]}
      />

      {move.error ? <Notice tone="danger">{move.error.message}</Notice> : null}

      <ProjectMoney
        rollup={rollup}
        unratedMinutes={unratedMinutes}
        paidCents={paidCents}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard title={`Jobs (${jobs.length})`}>
          {jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No work orders on this project yet. Open a job and put it on this project
              from its page.
            </p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {jobs.map((j) => {
                const js = display(JOB_STATUS, j.status);
                return (
                  <li key={j.id} className="flex items-center gap-3 py-2">
                    <Link
                      to="/jobs/$jobId"
                      params={{ jobId: j.id }}
                      className="min-w-0 flex-1 truncate hover:underline"
                    >
                      <span className="text-muted-foreground">#{j.jobNumber}</span>{' '}
                      {j.title}
                    </Link>
                    <StatusBadge tone={js.tone}>{js.label}</StatusBadge>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>

        <SectionCard title={`Invoices (${invoices.length})`}>
          {invoices.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing billed on this project yet.
            </p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {invoices.map((i) => {
                const is = display(INVOICE_STATUS, i.status);
                return (
                  <li key={i.id} className="flex items-center gap-3 py-2">
                    <Link
                      to="/invoices/$invoiceId"
                      params={{ invoiceId: i.id }}
                      className="min-w-0 flex-1 truncate hover:underline"
                    >
                      {i.invoiceNumber ? `Invoice #${i.invoiceNumber}` : 'Draft invoice'}
                    </Link>
                    <StatusBadge tone={is.tone}>{is.label}</StatusBadge>
                    <span className="shrink-0 tabular-nums">{money(i.totalCents)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>

        <ProjectQuotesSection
          quotes={quotes}
          withheld={Boolean(quotesWithheld)}
          reason={quotesWithheld?.reason}
        />

        <SectionCard title={`Purchase orders (${purchaseOrders.length})`}>
          {purchaseOrders.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing ordered against this project.
            </p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {purchaseOrders.map((p) => {
                const ps = PO_STATUS[p.status] ?? {
                  label: p.status,
                  tone: 'neutral' as Tone,
                };
                return (
                  <li key={p.id} className="flex items-center gap-3 py-2">
                    <Link
                      to="/purchase-orders/$purchaseOrderId"
                      params={{ purchaseOrderId: p.id }}
                      className="min-w-0 flex-1 truncate hover:underline"
                    >
                      {formatPoNumber(p.poNumber)}
                    </Link>
                    <StatusBadge tone={ps.tone}>{ps.label}</StatusBadge>
                    <span className="shrink-0 tabular-nums">
                      {money(p.subtotalCents)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>
      </div>

      {project.description ? (
        <SectionCard title="Scope">
          <p className="whitespace-pre-wrap text-sm">{project.description}</p>
        </SectionCard>
      ) : null}
    </PageRoot>
  );
}

export function ProjectQuotesSection({
  quotes,
  withheld,
  reason,
}: {
  quotes: readonly {
    id: string;
    quoteNumber: number;
    revision: number;
    status: string;
    totalCents: number;
  }[];
  withheld: boolean;
  reason?: string;
}) {
  return (
    <SectionCard
      title={
        withheld
          ? quotes.length
            ? `Quotes · visible (${quotes.length})`
            : 'Quotes'
          : `Quotes (${quotes.length})`
      }
    >
      {withheld ? (
        <Notice tone="neutral">
          Quote access is limited for your role.{reason ? ` ${reason}` : ''}
        </Notice>
      ) : null}
      {quotes.length === 0 ? (
        !withheld ? (
          <p className="text-sm text-muted-foreground">No quotes on this project.</p>
        ) : null
      ) : (
        <ul className="divide-y divide-border text-sm">
          {quotes.map((qt) => {
            const qs = display(QUOTE_STATUS, qt.status);
            return (
              <li key={qt.id} className="flex items-center gap-3 py-2">
                <Link
                  to="/quotes/$quoteId"
                  params={{ quoteId: qt.id }}
                  className="min-w-0 flex-1 truncate hover:underline"
                >
                  Quote #{qt.quoteNumber}
                  {qt.revision > 1 ? ` · rev ${qt.revision}` : ''}
                </Link>
                <StatusBadge tone={qs.tone}>{qs.label}</StatusBadge>
                <span className="shrink-0 tabular-nums">{money(qt.totalCents)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}

const BASIS_LABEL: Record<string, string> = {
  invoiced: 'vs. invoiced',
  quoted: 'vs. approved quotes',
  none: 'nothing billed yet',
};

export function ProjectMoney({
  rollup,
  unratedMinutes,
  paidCents,
}: {
  rollup: {
    partsCostCents: number;
    otherCostCents: number;
    laborCostCents: number;
    committedCents: number;
    costToDateCents: number;
    projectedCostCents: number;
    revenueCents: number;
    marginCents: number;
    marginPct: number | null;
    basis: CostingBasis;
  };
  unratedMinutes: number;
  paidCents: number;
}) {
  // What each figure beside the margin is measured over, said under it (`costing/basis.ts`).
  const measuredAgainst = revenueFigure('project', rollup.basis);
  const rows = [
    { label: 'Parts', value: rollup.partsCostCents },
    { label: 'Labor', value: rollup.laborCostCents },
    { label: 'Other costs', value: rollup.otherCostCents },
  ];

  return (
    <SectionCard title="Money on this project">
      <div className="grid gap-6 md:grid-cols-3">
        <div>
          <p className="text-sm text-muted-foreground">
            {rollup.basis === 'none' ? 'Cost so far' : 'Margin'}
          </p>
          <p
            className={`font-heading text-3xl font-bold tabular-nums ${
              rollup.basis === 'none'
                ? ''
                : rollup.marginCents < 0
                  ? 'text-[var(--sw-danger-text)]'
                  : 'text-[var(--sw-success-text)]'
            }`}
          >
            {money(rollup.basis === 'none' ? rollup.costToDateCents : rollup.marginCents)}
          </p>
          <p className="text-xs text-muted-foreground">
            {rollup.marginPct === null
              ? BASIS_LABEL[rollup.basis]
              : `${rollup.marginPct.toFixed(1)}% ${BASIS_LABEL[rollup.basis]}`}
          </p>
        </div>

        <div className="space-y-1 text-sm">
          {rows.map((r) => (
            <div key={r.label} className="flex justify-between gap-3">
              <span className="text-muted-foreground">{r.label}</span>
              <span className="tabular-nums">{money(r.value)}</span>
            </div>
          ))}
          <div className="flex justify-between gap-3 border-t border-border pt-1 font-medium">
            <span>Cost to date</span>
            <span className="tabular-nums">{money(rollup.costToDateCents)}</span>
          </div>
          {rollup.committedCents > 0 ? (
            <>
              <div className="flex justify-between gap-3">
                {/* Deliberately outside cost — ordering is not spending. */}
                <span className="text-muted-foreground">On order (not yet a cost)</span>
                <span className="tabular-nums">{money(rollup.committedCents)}</span>
              </div>
              <div className="flex justify-between gap-3 font-medium">
                <span>Projected cost</span>
                <span className="tabular-nums">{money(rollup.projectedCostCents)}</span>
              </div>
            </>
          ) : null}
        </div>

        <div className="space-y-1 text-sm">
          <div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">{measuredAgainst.label}</span>
              <span className="tabular-nums">
                {rollup.basis === 'none' ? '—' : money(rollup.revenueCents)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">{measuredAgainst.line}</p>
          </div>
          <div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Collected</span>
              <span className="tabular-nums">{money(paidCents)}</span>
            </div>
            <p className="text-xs text-muted-foreground">{COLLECTED_BASIS}</p>
          </div>
        </div>
      </div>

      {unratedMinutes > 0 ? (
        <Notice tone="warn">
          {formatMinutes(unratedMinutes)} of labor was logged by someone with no cost rate
          on file, so it counts as costing nothing. The margin above is flattering itself
          by that much — set rates in Settings.
        </Notice>
      ) : null}
    </SectionCard>
  );
}
